import { CameraManager } from '../camera/CameraManager.js';
import { CameraFrameMonitor } from '../camera/CameraFrameMonitor.js';
import { DiagnosticLogger } from '../diagnostics/DiagnosticLogger.js';
import { MetricsCollector } from '../diagnostics/MetricsCollector.js';
import { EventBus } from '../utils/EventBus.js';
import {
    isExpectedDecodeMiss,
    isRecoveryEligible,
    normalizeError,
    serializeError,
} from '../utils/errors.js';
import { AdapterManager } from './AdapterManager.js';
import { DetectionProcessor } from './DetectionProcessor.js';
import { RecoveryController } from './RecoveryController.js';

const perfNow = () => globalThis.performance?.now?.() ?? Date.now();

export class ScannerEngine {
    constructor({
        video,
        cooldownMs = 1200,
        maxRecoveryAttempts = 3,
        recoveryDelayMs = 800,
        logger = new DiagnosticLogger(),
        navigatorLike = globalThis.navigator,
        windowLike = globalThis.window,
        documentLike = globalThis.document,
    } = {}) {
        this.video = video;
        this.logger = logger;
        this.navigator = navigatorLike;
        this.window = windowLike;
        this.document = documentLike;
        this.events = new EventBus();
        this.metrics = new MetricsCollector();
        this.camera = new CameraManager({
            logger,
            navigatorLike,
            windowLike,
            onTrackEnded: () => this.handleTrackEnded(),
        });
        this.cameraFrameMonitor = new CameraFrameMonitor({
            windowLike,
            onFrame: (timestamp) => {
                this.metrics.recordCameraFrame(timestamp);
                this.emitMetrics();
            },
        });
        this.adapterManager = new AdapterManager({
            logger,
            windowLike,
        });
        this.adapter = null;
        this.adapterName = null;
        this.adapterDetails = null;
        this.status = 'idle';
        this.currentCamera = null;
        this.sessionToken = 0;
        this.explicitStop = false;
        this.suppressTrackEnded = false;
        this.nativeRuntimeFailures = 0;
        this.detectionLocked = false;
        this.adapterFallbackPending = false;
        this.pauseAfterRecovery = false;
        this.lastMetricsEmission = 0;
        this.lastLowFpsWarning = 0;
        this.unsubscribeLogger = this.logger.onEntry((entry) => {
            this.events.emit('log', entry);
        });
        this.detections = new DetectionProcessor({
            cooldownMs,
            metrics: this.metrics,
            logger,
            getAdapterName: () => this.adapterName,
            onResult: (result) => this.events.emit('result', result),
            onDuplicate: (result) => this.events.emit('duplicate', result),
            onMetricsChanged: () => this.emitMetrics(true),
        });
        this.recovery = new RecoveryController({
            windowLike,
            logger,
            maxAttempts: maxRecoveryAttempts,
            baseDelayMs: recoveryDelayMs,
            onScheduled: ({
                attempt,
                delay,
                requestedBy,
                component,
            }) => {
                this.metrics.recordRecovery();
                this.emitMetrics(true);
                this.setStatus('recovering', {
                    attempt,
                    delay,
                    requestedBy,
                    component,
                });
            },
            onExhausted: (error) => {
                this.setStatus('error', {
                    error: serializeError(error),
                });
            },
            onRecover: (token) => this.recover(token),
            isSessionCurrent: (token) => (
                this.isCurrentSession(token)
                && !this.explicitStop
            ),
        });

        this.handleVisibilityChange = () => {
            if (
                this.document?.visibilityState === 'visible'
                && ['running', 'result'].includes(this.status)
                && this.camera.track?.readyState === 'ended'
            ) {
                this.handleTrackEnded();
            }
        };
        this.handleDeviceChange = () => {
            this.logger.info(
                'camera.devices.changed',
                'O navegador informou uma mudança nos dispositivos de câmera.'
            );
            this.events.emit('deviceschanged');
        };

        this.document?.addEventListener?.(
            'visibilitychange',
            this.handleVisibilityChange
        );
        this.navigator?.mediaDevices?.addEventListener?.(
            'devicechange',
            this.handleDeviceChange
        );

        this.logger.info(
            'scanner.initialized',
            'Scanner Engine inicializado.',
            { cooldownMs, maxRecoveryAttempts }
        );
    }

    on(type, listener) {
        return this.events.on(type, listener);
    }

    get recoveryAttempts() {
        return this.recovery.attempts;
    }

    async discoverCameras(options = {}) {
        const cameras = await this.camera.discover(options);

        this.events.emit('cameras', cameras);

        return cameras;
    }

    async start({ deviceId = null, reason = 'initial' } = {}) {
        const token = ++this.sessionToken;

        this.explicitStop = false;
        this.recovery.clear();
        this.stopResources();
        this.currentCamera = null;
        this.metrics.reset();
        this.detections.reset();
        this.recovery.reset();
        this.nativeRuntimeFailures = 0;
        this.detectionLocked = false;
        this.adapterFallbackPending = false;
        this.pauseAfterRecovery = false;
        this.lastLowFpsWarning = 0;
        this.setStatus('initializing');

        this.logger.info(
            'scanner.starting',
            'Iniciando o Scanner Engine.',
            { requestedDeviceId: deviceId, reason }
        );

        try {
            const cameras = await this.discoverCameras({
                requestPermission: true,
                probeCapabilities: false,
            });

            if (!this.isCurrentSession(token)) {
                return null;
            }

            const selected = (
                cameras.find((camera) => camera.deviceId === deviceId)
                || cameras[0]
            );

            await this.openCameraAndAdapter(
                token,
                selected?.deviceId || deviceId || null
            );

            this.logger.info(
                'scanner.started',
                `Scanner iniciado com ${this.adapterName}.`,
                {
                    adapter: this.adapterName,
                    camera: this.currentCamera?.label,
                }
            );
            this.logger.info(
                'camera.trace.14_scanner_started',
                '[14] Scanner iniciado.',
                {
                    adapter: this.adapterName,
                    camera: this.currentCamera,
                    status: 'running',
                }
            );
            this.setStatus('running');

            return this.getSnapshot();
        } catch (error) {
            if (!this.isCurrentSession(token)) {
                return null;
            }

            const normalized = normalizeError(error, 'scanner.start');

            this.metrics.recordError();
            const canRecover = isRecoveryEligible(normalized);

            if (canRecover) {
                this.logger.error(
                    'scanner.start.failed',
                    normalized.message,
                    normalized
                );
            } else {
                this.logger.fatal(
                    'scanner.start.impossible',
                    normalized.message,
                    normalized
                );
            }
            this.events.emit('error', serializeError(normalized));
            this.emitMetrics(true);
            this.stopResources();

            if (canRecover) {
                this.scheduleRecovery(normalized, {
                    requestedBy: 'ScannerEngine.start',
                    component: 'camera.start',
                });
            } else {
                this.setStatus('error', {
                    error: serializeError(normalized),
                });
            }

            throw normalized;
        }
    }

    async openCameraAndAdapter(token, deviceId) {
        this.metrics.markCameraOpening();
        const cameraInfo = await this.camera.open(this.video, deviceId);

        if (!this.isCurrentSession(token)) {
            this.camera.stop();
            return;
        }

        this.metrics.markCameraOpen();
        this.currentCamera = cameraInfo;
        this.cameraFrameMonitor.start(this.video);
        this.events.emit('camera', this.currentCamera);
        this.emitMetrics(true);

        const autofocus = await this.camera.enableContinuousAutofocus();

        if (autofocus.error) {
            this.metrics.recordError();
        }

        this.currentCamera = this.camera.getCurrentInfo();
        this.events.emit('camera', this.currentCamera);

        if (this.metrics.scannerStartedAt === null) {
            this.metrics.markScannerStarted();
        }
        await this.startBestAdapter(token);
    }

    async startBestAdapter(token) {
        const selected = await this.adapterManager.start({
            stream: this.camera.stream,
            video: this.video,
            nativeRuntimeFailures: this.nativeRuntimeFailures,
            onDetected: (result) => {
                if (this.isCurrentSession(token)) {
                    this.handleDetection(result);
                }
            },
            onFrame: (frame = {}) => {
                if (this.isCurrentSession(token)) {
                    this.metrics.recordProcessingFrame({
                        durationMs: frame.durationMs,
                        detected: frame.detected,
                    });
                    this.logger.debugThrottled(
                        'scanner.frame.processed',
                        'Frame processado.',
                        {
                            detected: Boolean(frame.detected),
                            detectedCount: frame.detectedCount || 0,
                            durationMs: frame.durationMs,
                        }
                    );

                    if (!frame.detected) {
                        this.logger.debugThrottled(
                            'scanner.frame.no_code',
                            'Frame sem código.',
                            {
                                total: this.metrics.noCodeFrameCount,
                            }
                        );
                    }

                    this.emitMetrics();
                }
            },
            onError: (error, options) => {
                if (this.isCurrentSession(token)) {
                    this.handleAdapterError(error, options);
                }
            },
            onStartupError: (error, adapterName) => {
                this.metrics.recordError();
                this.logger.error(
                    'adapter.start.failed',
                    `${adapterName} falhou ao iniciar.`,
                    error
                );
                this.events.emit('error', serializeError(error));
            },
            processingFrameRate:
                this.camera.getAdaptiveProfile().target.processingFrameRate,
        });

        if (!this.isCurrentSession(token)) {
            selected.adapter.stop();
            return;
        }

        this.adapter = selected.adapter;
        this.adapterName = selected.name;
        this.adapterDetails = selected.details;
        this.events.emit('adapter', {
            name: selected.name,
            details: selected.details,
        });
    }

    handleDetection(result) {
        const text = String(result?.text || '').trim();

        if (
            !text
            || this.detectionLocked
            || this.status !== 'running'
        ) {
            return;
        }

        this.detectionLocked = true;
        this.adapter?.pause?.();
        this.setStatus('result');
        this.detections.handle(result);
    }

    async resume() {
        if (this.status !== 'result' || !this.adapter) {
            return this.getSnapshot();
        }

        this.detections.allowNextRead();
        this.detectionLocked = false;
        this.pauseAfterRecovery = false;
        this.setStatus('initializing', { phase: 'resuming-reading' });

        try {
            await this.adapter.resume?.();
            this.setStatus('running');
            this.logger.info(
                'scanner.reading.resumed',
                'Leitura reiniciada sem reabrir a câmera.'
            );

            return this.getSnapshot();
        } catch (error) {
            const normalized = normalizeError(
                error,
                `adapter.${this.adapterName || 'unknown'}.resume`
            );

            this.handleAdapterError(normalized, { fatal: true });
            throw normalized;
        }
    }

    handleAdapterError(error, { fatal = false } = {}) {
        if (isExpectedDecodeMiss(error)) {
            this.logger.debugThrottled(
                'scanner.read.no_result_ignored',
                'Exceção normal de decodificação ignorada.',
                {
                    adapter: this.adapterName,
                    name: error?.name
                        || error?.constructor?.name
                        || null,
                    kind: error?.getKind?.()
                        || error?.kind
                        || error?.details?.originalKind
                        || null,
                    message: error?.message
                        || error?.details?.originalMessage
                        || null,
                }
            );
            return;
        }

        const normalized = normalizeError(error, 'scanner.read');

        this.metrics.recordError();
        this.logger.error(
            'scanner.read.error',
            normalized.message,
            normalized,
            { adapter: this.adapterName, fatal }
        );
        this.events.emit('error', serializeError(normalized));
        this.emitMetrics(true);

        if (fatal) {
            if (this.adapterName === 'BarcodeDetector') {
                this.attemptAdapterFallback(normalized);
                return;
            }

            if (isRecoveryEligible(normalized)) {
                this.scheduleRecovery(normalized, {
                    requestedBy: 'ScannerEngine.handleAdapterError',
                    component: `adapter.${this.adapterName || 'unknown'}`,
                });
            } else {
                this.setStatus('error', {
                    error: serializeError(normalized),
                });
            }
        }
    }

    async attemptAdapterFallback(error) {
        if (this.adapterFallbackPending || this.explicitStop) {
            return;
        }

        this.adapterFallbackPending = true;
        const token = this.sessionToken;
        this.nativeRuntimeFailures++;
        this.adapter?.stop?.();
        this.adapter = null;
        this.adapterName = null;
        this.adapterDetails = null;
        this.setStatus('initializing', { phase: 'detector-fallback' });

        try {
            await this.startBestAdapter(token);

            if (!this.isCurrentSession(token)) {
                return;
            }

            this.setStatus('running');
            this.logger.info(
                'adapter.fallback.completed',
                'Detector alternado sem reiniciar a câmera.',
                { reason: error.message, adapter: this.adapterName }
            );
        } catch (fallbackError) {
            const normalized = normalizeError(
                fallbackError,
                'adapter.fallback'
            );

            this.events.emit('error', serializeError(normalized));
            const scheduled = this.scheduleRecovery(normalized, {
                requestedBy: 'ScannerEngine.attemptAdapterFallback',
                component: 'adapter.fallback',
            });

            if (!scheduled) {
                this.setStatus('error', {
                    error: serializeError(normalized),
                });
            }
        } finally {
            this.adapterFallbackPending = false;
        }
    }

    handleTrackEnded() {
        if (
            this.explicitStop
            || this.suppressTrackEnded
            || !['running', 'result', 'recovering'].includes(this.status)
        ) {
            return;
        }

        const health = this.getCameraHealth();

        if (health.healthy) {
            this.logger.warn(
                'camera.track.ended_ignored',
                'Evento ended ignorado porque a track e o vídeo continuam ativos.',
                {
                    requestedBy: 'ScannerEngine.handleTrackEnded',
                    component: 'camera.track',
                    health,
                }
            );
            return;
        }

        const error = normalizeError(
            new DOMException(
                'A faixa de vídeo foi encerrada inesperadamente.',
                'AbortError'
            ),
            'camera.track'
        );

        this.metrics.recordError();
        this.logger.error(
            'camera.track.ended',
            'A câmera foi encerrada inesperadamente.',
            error
        );
        this.events.emit('error', serializeError(error));
        this.scheduleRecovery(error, {
            requestedBy: 'ScannerEngine.handleTrackEnded',
            component: 'camera.track',
        });
    }

    getCameraHealth() {
        const track = this.camera.track;
        const stream = this.camera.stream;
        const video = this.camera.video || this.video;
        const trackReadyState = track?.readyState || null;
        const videoReadyState = Number(video?.readyState) || 0;
        const sameStream = Boolean(
            stream
            && video?.srcObject
            && video.srcObject === stream
        );

        return {
            healthy: (
                trackReadyState === 'live'
                && videoReadyState >= 2
                && sameStream
            ),
            trackReadyState,
            trackEnabled: track?.enabled ?? null,
            trackMuted: track?.muted ?? null,
            streamActive: stream?.active ?? null,
            videoReadyState,
            videoPaused: video?.paused ?? null,
            videoEnded: video?.ended ?? null,
            videoHasSrcObject: Boolean(video?.srcObject),
            sameStream,
        };
    }

    scheduleRecovery(error, {
        requestedBy = 'ScannerEngine',
        component = null,
    } = {}) {
        const normalized = normalizeError(error, component || 'scanner');
        const health = this.getCameraHealth();
        const sourceError = normalized.cause || error;
        const request = {
            requestedBy,
            component: component || normalized.context,
            exception: {
                ...serializeError(normalized),
                originalName: sourceError?.name
                    || sourceError?.constructor?.name
                    || null,
                originalMessage: sourceError?.message || null,
                stack: sourceError?.stack || normalized.stack || null,
            },
            health,
        };

        this.logger.info(
            'scanner.recovery.requested',
            'Solicitação de recovery recebida.',
            request
        );

        if (this.explicitStop) {
            this.logger.debug(
                'scanner.recovery.ignored_explicit_stop',
                'Recovery ignorado porque o scanner foi encerrado.',
                request
            );
            return false;
        }

        if (isExpectedDecodeMiss(normalized)) {
            this.logger.debug(
                'scanner.recovery.ignored_decode_miss',
                'Recovery ignorado: o frame apenas não contém código.',
                request
            );
            return false;
        }

        if (!isRecoveryEligible(normalized)) {
            this.logger.warn(
                'scanner.recovery.ignored_ineligible',
                'Recovery ignorado: a exceção não representa falha de câmera.',
                request
            );
            return false;
        }

        if (health.healthy) {
            this.logger.warn(
                'scanner.recovery.ignored_healthy_camera',
                'Recovery ignorado: MediaStream, track e vídeo continuam saudáveis.',
                request
            );
            return false;
        }

        this.pauseAfterRecovery = (
            this.pauseAfterRecovery
            || this.detectionLocked
            || this.status === 'result'
        );
        this.recovery.schedule(
            normalized,
            this.sessionToken,
            request
        );

        return true;
    }

    async recover(token) {
        const deviceId = (
            this.currentCamera?.deviceId
            || this.currentCamera?.settings?.deviceId
            || null
        );

        const shouldPause = this.pauseAfterRecovery;

        this.stopResources();
        this.detectionLocked = shouldPause;

        try {
            await this.openCameraAndAdapter(token, deviceId);

            if (!this.isCurrentSession(token)) {
                return;
            }

            this.recovery.reset();

            if (shouldPause) {
                this.adapter?.pause?.();
                this.setStatus('result');
            } else {
                this.setStatus('running');
            }

            this.pauseAfterRecovery = false;
            this.logger.info(
                'scanner.recovery.completed',
                shouldPause
                    ? 'Câmera recuperada; leitura permanece pausada aguardando o usuário.'
                    : 'Scanner retomado automaticamente.'
            );
        } catch (error) {
            const normalized = normalizeError(error, 'scanner.recovery');

            this.metrics.recordError();
            this.logger.error(
                'scanner.recovery.failed',
                normalized.message,
                normalized,
                { attempt: this.recoveryAttempts }
            );
            this.events.emit('error', serializeError(normalized));
            this.emitMetrics(true);
            const scheduled = this.scheduleRecovery(normalized, {
                requestedBy: 'ScannerEngine.recover',
                component: 'camera.recovery',
            });

            if (!scheduled) {
                this.setStatus('error', {
                    error: serializeError(normalized),
                });
            }
        }
    }

    async switchCamera(deviceId) {
        this.logger.info(
            'camera.switch.requested',
            'Mudança manual de câmera solicitada.',
            { deviceId }
        );

        const token = ++this.sessionToken;

        this.explicitStop = false;
        this.recovery.clear();
        this.stopResources();
        this.detectionLocked = false;
        this.pauseAfterRecovery = false;
        this.detections.allowNextRead();
        this.setStatus('switching');

        try {
            await this.openCameraAndAdapter(token, deviceId || null);

            if (!this.isCurrentSession(token)) {
                return null;
            }

            this.setStatus('running');
            this.logger.info(
                'camera.switch.completed',
                'Câmera alterada sem reinicializar o Scanner Engine.',
                { deviceId: this.currentCamera?.deviceId }
            );

            return this.getSnapshot();
        } catch (error) {
            const normalized = normalizeError(error, 'camera.switch');

            this.metrics.recordError();
            this.events.emit('error', serializeError(normalized));
            const scheduled = this.scheduleRecovery(normalized, {
                requestedBy: 'ScannerEngine.switchCamera',
                component: 'camera.switch',
            });

            if (!scheduled) {
                this.setStatus('error', {
                    error: serializeError(normalized),
                });
            }
            throw normalized;
        }
    }

    async setTorch(enabled) {
        try {
            this.currentCamera = await this.camera.setTorch(enabled);
            this.events.emit('camera', this.currentCamera);

            return this.currentCamera;
        } catch (error) {
            throw this.reportControlError(error, 'camera.torch');
        }
    }

    async setZoom(value) {
        try {
            this.currentCamera = await this.camera.setZoom(value);
            this.events.emit('camera', this.currentCamera);

            return this.currentCamera;
        } catch (error) {
            throw this.reportControlError(error, 'camera.zoom');
        }
    }

    reportControlError(error, context) {
        const normalized = normalizeError(error, context);

        this.metrics.recordError();
        this.logger.error(
            `${context}.failed`,
            normalized.message,
            normalized
        );
        this.events.emit('error', serializeError(normalized));
        this.emitMetrics(true);

        return normalized;
    }

    setCooldown(cooldownMs) {
        this.detections.setCooldown(cooldownMs);
        this.logger.info(
            'scanner.cooldown.changed',
            `Cooldown alterado para ${this.detections.cooldownMs} ms.`
        );
        this.events.emit('configuration', {
            cooldownMs: this.detections.cooldownMs,
        });
    }

    stop() {
        this.explicitStop = true;
        this.sessionToken++;
        this.recovery.clear();
        this.stopResources();
        this.detectionLocked = false;
        this.pauseAfterRecovery = false;
        this.setStatus('stopped');
        this.logger.info(
            'scanner.stopped',
            'Scanner interrompido e câmera liberada.'
        );
    }

    stopResources() {
        this.suppressTrackEnded = true;
        this.cameraFrameMonitor.stop();
        this.adapter?.stop?.();
        this.adapter = null;
        this.adapterName = null;
        this.adapterDetails = null;
        this.camera.stop();
        this.suppressTrackEnded = false;
    }

    isCurrentSession(token) {
        return token === this.sessionToken && !this.explicitStop;
    }

    setStatus(status, details = {}) {
        this.status = status;
        this.events.emit('statechange', { status, ...details });
    }

    emitMetrics(force = false) {
        const timestamp = perfNow();

        if (!force && timestamp - this.lastMetricsEmission < 250) {
            return;
        }

        this.lastMetricsEmission = timestamp;
        const snapshot = this.metrics.snapshot(timestamp);

        this.events.emit('metrics', snapshot);

        if (
            this.status === 'running'
            && snapshot.processingFrameCount >= 20
            && snapshot.processingFps > 0
            && snapshot.processingFps < 5
            && timestamp - this.lastLowFpsWarning > 10000
        ) {
            this.lastLowFpsWarning = timestamp;
            this.logger.warn(
                'scanner.performance.low_fps',
                'FPS de processamento baixo.',
                { processingFps: snapshot.processingFps }
            );
        }
    }

    getSnapshot() {
        return {
            status: this.status,
            readingActive: this.status === 'running',
            adapter: this.adapterName,
            adapterDetails: this.adapterDetails,
            cooldownMs: this.detections.cooldownMs,
            recoveryAttempts: this.recoveryAttempts,
            cameras: this.camera.getCameras(),
            currentCamera: this.camera.getCurrentInfo() || this.currentCamera,
            adaptiveProfile: this.camera.getAdaptiveProfile(),
            lastResult: this.detections.lastResult
                ? { ...this.detections.lastResult }
                : null,
            metrics: this.metrics.snapshot(),
        };
    }

    getDiagnosticReport(environment = null) {
        const snapshot = this.getSnapshot();
        const logs = this.logger.getEntries();

        return {
            generatedAt: new Date().toISOString(),
            compatibility: environment,
            capabilities: snapshot.currentCamera?.capabilities || null,
            performance: snapshot.metrics,
            camera: {
                count: snapshot.cameras.length,
                selected: snapshot.currentCamera,
                available: snapshot.cameras,
                adaptiveProfile: snapshot.adaptiveProfile,
            },
            detector: {
                selected: snapshot.adapter,
                details: snapshot.adapterDetails,
                nativeRuntimeFailures: this.nativeRuntimeFailures,
            },
            recovery: {
                attempts: snapshot.recoveryAttempts,
                total: snapshot.metrics.recoveryCount,
                active: snapshot.status === 'recovering',
            },
            reading: {
                active: snapshot.readingActive,
                state: snapshot.status,
                lastResult: snapshot.lastResult,
                count: snapshot.metrics.readCount,
            },
            errors: logs.filter((entry) => (
                entry.level === 'error' || entry.level === 'fatal'
            )),
            scanner: snapshot,
            logs,
        };
    }

    destroy() {
        this.stop();
        this.unsubscribeLogger?.();
        this.document?.removeEventListener?.(
            'visibilitychange',
            this.handleVisibilityChange
        );
        this.navigator?.mediaDevices?.removeEventListener?.(
            'devicechange',
            this.handleDeviceChange
        );
        this.events.clear();
    }
}
